"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView, MotionConfig } from "framer-motion";
import dynamic from "next/dynamic";
import Link from "next/link";
import "./landing.css";
import { useLang } from "./i18n";
import { START_HREF, contactHref } from "@/lib/routes";
import { trackCta } from "@/lib/analytics";
import { TIER_PRICING } from "@/lib/stripe/tierFeatures";
import SiteNav from "@/components/marketing/SiteNav";
import SiteFooter from "@/components/marketing/SiteFooter";
import HoverClip from "@/components/landing/HoverClip";

const HeroExperience = dynamic(
  () => import("@/components/landing/HeroExperience"),
  {
    ssr: false,
    // Reserve the hero's full scroll height (680vh desktop / 100dvh mobile,
    // via the same CSS class) while the chunk loads. Without this, sections
    // below (#preview, #who, …) sit ~4 viewport-heights too high at
    // hash-scroll time and shift down when the hero mounts — which is why
    // /#who deep-links used to strand visitors at the top.
    loading: () => <div className="hero-scroll-spacer" aria-hidden="true" />,
  }
);

// Scanned-building teaser — client-only WebGL, a taste of the methodology dive.
const LandingScanTeaser = dynamic(
  () => import("@/components/landing/LandingScanTeaser"),
  { ssr: false }
);

// ScoreAnatomy moved to /methodology — replaced with compact teaser below

/* ── Animation helpers ── */

function FadeIn({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

/* ── Lazy-mounted scan teaser — only spins up the WebGL canvas once the
   section scrolls within ~300px of the viewport, so it never competes with
   the hero video for the GPU on first paint. ── */
function ScanTeaserMount() {
  const ref = useRef<HTMLDivElement>(null);
  const near = useInView(ref, { once: true, margin: "300px" });
  return (
    <div
      ref={ref}
      className="relative w-full h-[340px] sm:h-[460px]"
    >
      {near && <LandingScanTeaser />}
    </div>
  );
}

function StaggerGrid({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.07 } },
      }}
    >
      {children}
    </motion.div>
  );
}

function StaggerItem({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

/* ── FAQ accordion ── */

function FaqItem({
  q,
  a,
  isOpen,
  onToggle,
}: {
  q: string;
  a: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-brand-border last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-6 py-5 sm:py-6 text-left group"
      >
        <span className="font-display text-[1.1rem] sm:text-[1.25rem] font-semibold text-brand-linen leading-[1.35] tracking-[0.005em] group-hover:text-brand-meadow transition-colors">
          {q}
        </span>
        <span
          className={`flex-shrink-0 w-6 h-6 rounded-full border flex items-center justify-center transition-all duration-300 ${
            isOpen
              ? "border-brand-meadow bg-brand-meadow/10"
              : "border-brand-border group-hover:border-brand-muted"
          }`}
        >
          <svg
            viewBox="0 0 16 16"
            className="w-3 h-3 stroke-current"
            fill="none"
            strokeWidth={2}
            strokeLinecap="round"
            style={{
              color: isOpen ? "#4CAF7A" : "#5C7A68",
              transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
              transition: "transform 300ms cubic-bezier(0.25, 0.1, 0.25, 1), color 300ms",
            }}
          >
            <line x1="8" y1="2" x2="8" y2="14" />
            <line x1="2" y1="8" x2="14" y2="8" />
          </svg>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <p className="text-[0.95rem] text-brand-linen/75 leading-[1.75] pr-10 pb-5 sm:pb-6 max-w-[640px]">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function LandingContent() {
  const { t, lang } = useLang();
  const [openFaq, setOpenFaq] = useState<number>(0);

  // Locale-aware audience page URLs
  const audienceHref = {
    developers: lang === "nl" ? "/nl/voor-ontwikkelaars" : "/for-developers",
    brokers: lang === "nl" ? "/nl/voor-makelaars" : "/for-brokers",
    homebuyers: lang === "nl" ? "/nl/voor-kopers" : "/for-homebuyers",
    planners: lang === "nl" ? "/nl/voor-gemeenten" : "/for-planners",
    dataSources: lang === "nl" ? "/nl/data-sources" : "/data-sources",
    preview: lang === "nl" ? "/nl/preview" : "/preview",
  };
  const learnMore = lang === "nl" ? "Lees meer" : "Learn more";

  useEffect(() => {
    document.body.classList.add("landing-body");
    return () => {
      document.body.classList.remove("landing-body");
    };
  }, []);

  // Hash deep-links (/#who, /#faq, …). Section offsets are stable from
  // first paint (the hero's dynamic import reserves its scroll height, see
  // above), but Next's built-in hash scroll runs in a layout effect before
  // hydration settles and races the body's CSS smooth-scrolling — so after a
  // client-side navigation from another route it can leave the viewport at
  // the top. Re-assert once on mount (instant: arriving from another page
  // should land, not travel 5 viewport-heights), and follow `hashchange` for
  // native in-page anchor jumps. `scrollIntoView` honours each section's
  // scroll-mt-*, keeping the fixed nav clear of the headline.
  useEffect(() => {
    const scrollToHash = (behavior: ScrollBehavior) => {
      const id = window.location.hash.slice(1);
      if (!id) return;
      document.getElementById(id)?.scrollIntoView({ behavior });
    };
    scrollToHash("instant");
    // One frame later, re-assert over any smooth scroll Next kicked off in
    // its own navigation handling (an in-flight animation would otherwise
    // keep gliding after our instant jump).
    const raf = requestAnimationFrame(() => scrollToHash("instant"));
    const onHashChange = () => scrollToHash("smooth");
    window.addEventListener("hashchange", onHashChange);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  return (
    /* reducedMotion="user" drops the travel from every FadeIn/StaggerItem on
       the page while leaving the opacity fade alone — which is the distinction
       the preference actually draws. It's vestibular motion that hurts; a
       cross-fade doesn't. One wrapper beats threading useReducedMotion through
       every animated component, and it can't be forgotten by the next one. */
    <MotionConfig reducedMotion="user">
    <div className="font-brand antialiased">
      {/* ── SEO h1 — server-rendered (HeroExperience is ssr:false, so its
          visual headline isn't in the initial HTML that Google crawls). Short,
          keyword-forward, visually hidden but present for crawlers and
          screen readers. ── */}
      <h1 className="sr-only">
        {t("seo.h1")}
      </h1>

      {/* ── Navigation (shared across all public pages) ── */}
      {/* onDarkHero: HeroExperience renders a dark video behind the bar. */}
      <SiteNav
        locale={lang}
        active={null}
        altHref={lang === "en" ? "/nl" : "/"}
        onDarkHero
      />

      <main>
      {/* ── Scroll-Driven Video Hero ── */}
      <HeroExperience />

      {/* Everything after the hero is one sheet that rides up OVER the
          hero's resting frame for the last viewport-height of its pin
          (landing.css .hero-sheet, SHEET_OVERLAP_VH in heroLayers.ts): the
          film ends by the page arriving, not by the hero scrolling away. It
          has to be positioned above the sticky container, so the wrapper is
          the whole rest of <main>, not just the first section. */}
      <div className="hero-sheet">

      {/* ── How we see a plot — scanned-building teaser (methodology hint) ── */}
      <section id="how" className="py-16 sm:py-24 border-b border-brand-border overflow-hidden scroll-mt-20" style={{ background: "rgb(var(--c-surface) / 0.4)" }}>
        <div className="max-w-[920px] mx-auto px-6 sm:px-8">
          <div className="text-center max-w-[680px] mx-auto">
            <FadeIn>
              <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.18em] mb-5">
                {lang === "nl" ? "Hoe PlotScore een perceel ziet" : "How PlotScore sees a plot"}
              </p>
            </FadeIn>
            <FadeIn delay={0.05}>
              <h2 className="font-display text-[2.1rem] sm:text-[3rem] md:text-[3.5rem] font-semibold text-brand-linen leading-[1.08] tracking-[-0.01em] mb-5">
                {lang === "nl" ? (
                  <>Wij schatten niet.<br /><span className="text-brand-meadow">Wij scoren.</span></>
                ) : (
                  <>{"We don't estimate."}<br /><span className="text-brand-meadow">We score.</span></>
                )}
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-[1.05rem] sm:text-[1.15rem] text-brand-linen/80 leading-[1.6] max-w-[560px] mx-auto">
                {lang === "nl"
                  ? "Elk perceel bouwen we op uit échte data — LiDAR-hoogtescans, 3D-gebouwgeometrie en tientallen officiële registers."
                  : "Every plot is rebuilt from real data — LiDAR height scans, 3D building geometry, and dozens of official registers."}
              </p>
            </FadeIn>
          </div>

          {/* 3D scanned-building teaser, framed as a scan viewport */}
          <FadeIn delay={0.12}>
            <div className="relative mt-9 mb-10">
              <ScanTeaserMount />
              {/* corner brackets for a techy 'scan viewport' frame */}
              <div className="pointer-events-none absolute inset-0">
                <span className="absolute top-0 left-0 w-5 h-5 border-t border-l border-brand-meadow/40" />
                <span className="absolute top-0 right-0 w-5 h-5 border-t border-r border-brand-meadow/40" />
                <span className="absolute bottom-0 left-0 w-5 h-5 border-b border-l border-brand-meadow/40" />
                <span className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-brand-meadow/40" />
              </div>
              <p className="text-center text-[0.7rem] text-brand-muted/80 font-data tracking-[0.1em] uppercase mt-2">
                {lang === "nl" ? "AHN LiDAR-puntenwolk · 3DBAG-massa" : "AHN LiDAR point cloud · 3DBAG massing"}
              </p>
            </div>
          </FadeIn>

          <div className="text-center max-w-[640px] mx-auto">
            <FadeIn delay={0.05}>
              <p className="font-display text-[1.35rem] sm:text-[1.65rem] font-semibold text-brand-linen leading-[1.2] mb-3">
                {lang === "nl" ? (
                  <>5 dimensies. 90+ factoren. <span className="text-brand-meadow">Eén getal.</span></>
                ) : (
                  <>Five dimensions. 90+ factors. <span className="text-brand-meadow">One number.</span></>
                )}
              </p>
            </FadeIn>
            <FadeIn delay={0.08}>
              <p className="text-[0.95rem] sm:text-[1rem] text-brand-linen/70 leading-[1.65] mb-7 max-w-[520px] mx-auto">
                {lang === "nl"
                  ? "Vastgoedwaarde, gebouwkwaliteit, locatie, buurt en investeringspotentieel — samengevat in één score die traditionele taxaties missen."
                  : "Property value, building quality, location, neighborhood, and investment potential — distilled into one score that captures what traditional valuations miss."}
              </p>
            </FadeIn>

            {/* CTAs — bold filled primary into the methodology dive */}
            <FadeIn delay={0.12}>
              <div className="flex items-center justify-center gap-5 flex-wrap">
                <Link
                  href="/methodology"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-brand-meadow text-on-accent text-[0.9rem] font-semibold rounded-lg hover:bg-brand-meadow-light shadow-[0_0_28px_-6px_rgba(76,175,122,0.55)] transition-all"
                >
                  {lang === "nl" ? "Zie hoe we je perceel lezen" : "See how we read your plot"}
                  <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 stroke-current fill-none" strokeWidth={1.85} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 8h10M9 4l4 4-4 4" />
                  </svg>
                </Link>
                <Link
                  href={audienceHref.dataSources}
                  className="inline-flex items-center gap-1.5 text-[0.8rem] text-brand-sage hover:text-brand-meadow transition-colors"
                >
                  {lang === "nl" ? "Alle databronnen" : "All data sources"}
                  <svg viewBox="0 0 16 16" className="w-3 h-3 stroke-current fill-none" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 8h10M9 4l4 4-4 4" />
                  </svg>
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

{/* ── Who It's For ── */}
      <section id="who" className="py-20 sm:py-32 bg-brand-pine scroll-mt-20">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
          <FadeIn>
            <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.15em] mb-3">
              {t("who.eyebrow")}
            </p>
          </FadeIn>
          <FadeIn delay={0.05}>
            <h2 className="font-display text-[2rem] sm:text-[2.5rem] font-semibold text-brand-linen leading-[1.2] tracking-[0.01em] mb-4">
              {t("who.headline")}
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-base text-brand-linen/80 max-w-[560px] mb-12 leading-[1.75]">
              {t("who.intro")}
            </p>
          </FadeIn>

          <StaggerGrid className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              // Reading order (2026-09-08): the person deciding on one house first,
              // then the people who sell it, build on it, and plan around it.
              {
                role: t("who.card3.role"),
                title: t("who.card3.title"),
                desc: t("who.card3.desc"),
                href: audienceHref.homebuyers,
                clip: "homebuyer" as const,
              },
              {
                role: t("who.card2.role"),
                title: t("who.card2.title"),
                desc: t("who.card2.desc"),
                href: audienceHref.brokers,
                clip: "broker" as const,
              },
              {
                role: t("who.card1.role"),
                title: t("who.card1.title"),
                desc: t("who.card1.desc"),
                href: audienceHref.developers,
                clip: "developer" as const,
              },
              {
                role: t("who.card4.role"),
                title: t("who.card4.title"),
                desc: t("who.card4.desc"),
                href: audienceHref.planners,
                clip: "city" as const,
              },
            ].map((uc) => (
              <StaggerItem key={uc.role}>
                <Link
                  href={uc.href}
                  className="group block border border-brand-border rounded-lg p-7 h-full transition-all hover:border-brand-meadow/45 hover:bg-brand-meadow/[0.03]"
                >
                  {/* The audience's end frame at rest; on hover the 5 s clip from the
                      plain house to that frame (HoverClip). */}
                  <HoverClip kind={uc.clip} alt={uc.role} />
                  <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.12em] mb-4">
                    {uc.role}
                  </p>
                  <h3 className="font-display text-[1.35rem] font-semibold text-brand-linen leading-[1.25] tracking-[0.005em] mb-3 group-hover:text-brand-meadow transition-colors">
                    {uc.title}
                  </h3>
                  <p className="text-[0.9rem] text-brand-linen/70 leading-[1.65] mb-5">
                    {uc.desc}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-[0.78rem] text-brand-sage group-hover:text-brand-meadow transition-colors">
                    {learnMore}
                    <svg
                      viewBox="0 0 16 16"
                      className="w-3 h-3 stroke-current fill-none transition-transform group-hover:translate-x-0.5"
                      strokeWidth={1.75}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 8h10M9 4l4 4-4 4" />
                    </svg>
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </StaggerGrid>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 sm:py-32">
        <div className="max-w-[880px] mx-auto px-5 sm:px-8">
          <FadeIn>
            <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.18em] mb-4">
              {t("faq.eyebrow")}
            </p>
          </FadeIn>
          <FadeIn delay={0.05}>
            <h2 className="font-display text-[2rem] sm:text-[2.5rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-4 max-w-[560px]">
              {t("faq.headline")}
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-base text-brand-linen/75 max-w-[560px] mb-10 sm:mb-14 leading-[1.75]">
              {t("faq.intro")}{" "}
              <a
                href={contactHref(lang === "nl" ? "Vraag over PlotScore" : "Question about PlotScore")}
                className="text-brand-sage hover:text-brand-meadow underline underline-offset-4 decoration-brand-border transition-colors"
              >
                {t("faq.contact")}
              </a>
            </p>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="border-t border-brand-border">
              {[1, 2, 3, 4, 5].map((n, idx) => (
                <FaqItem
                  key={n}
                  q={t(`faq.q${n}`)}
                  a={t(`faq.a${n}`)}
                  isOpen={openFaq === idx}
                  onToggle={() => setOpenFaq((prev) => (prev === idx ? -1 : idx))}
                />
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── The product pointer sits at the end, beside the sign-up: by the time
           a reader is here they have watched the score being made and read who
           it is for, and this is where "see it before you sign up" belongs. ── */}
      <section id="preview" className="py-24 sm:py-36 border-b border-brand-border text-center scroll-mt-20">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8">
          <FadeIn>
            <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.18em] mb-6">
              {lang === "nl" ? "Het product" : "The product"}
            </p>
          </FadeIn>
          <FadeIn delay={0.05}>
            <h2 className="font-display text-[2.5rem] sm:text-[3.5rem] md:text-[4rem] font-semibold text-brand-linen leading-[1.02] tracking-[-0.01em] mb-6 [text-wrap:balance]">
              {lang === "nl" ? "Zie het voordat je een account maakt." : "See it before you sign up."}
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-[1.05rem] sm:text-[1.2rem] text-brand-linen/75 leading-[1.6] max-w-[560px] mx-auto mb-10">
              {lang === "nl"
                ? "Kijk hoe een score ontstaat, open de lagen, blader door het dossier. Opgenomen uit de app — niets is een mockup."
                : "Watch a score being made, open the layers, page through the dossier. Recorded from the app — nothing is a mockup."}
            </p>
          </FadeIn>
          <FadeIn delay={0.14}>
            <Link
              href={audienceHref.preview}
              onClick={() => trackCta("landing-preview")}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-meadow text-on-accent text-[0.95rem] font-semibold rounded-lg hover:bg-brand-meadow-light shadow-[0_0_28px_-6px_rgba(76,175,122,0.55)] transition-all"
            >
              {lang === "nl" ? "Open de preview" : "Open the preview"}
              <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 stroke-current fill-none" strokeWidth={1.85} strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* ── Sign up ── */}
      <section id="signup" className="py-20 sm:py-32 text-center">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
          <FadeIn>
            <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.15em] mb-3">
              {t("signup.eyebrow")}
            </p>
          </FadeIn>
          <FadeIn delay={0.05}>
            <h2 className="font-display text-[2rem] sm:text-[2.5rem] font-semibold text-brand-linen leading-[1.2] tracking-[0.01em] mb-4 max-w-[600px] mx-auto">
              {t("signup.headline")}
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-base text-brand-linen/80 max-w-[480px] mx-auto mb-8 leading-[1.75]">
              {t("signup.intro")}
            </p>
          </FadeIn>
          {/* ── Price anchor.
               Until this shipped, a visitor could read the entire page without
               learning PlotScore costs anything: "free" appeared three times,
               the only euro figure in a section body was the €2T market size,
               and the real numbers lived in one FAQ row that opens collapsed.
               Prices come from TIER_PRICING so this can't drift from /pricing. ── */}
          <FadeIn delay={0.12}>
            <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 sm:gap-x-8 mb-8 max-w-[620px] mx-auto">
              {[
                { name: "Free", nlName: "Gratis", price: TIER_PRICING.free[lang], note: lang === "nl" ? "drie scores" : "three scores" },
                { name: "Buyer's Pass", nlName: "Buyer's Pass", price: TIER_PRICING.pass[lang], note: lang === "nl" ? "30 dagen" : "30 days" },
                { name: "Pro", nlName: "Pro", price: `€${TIER_PRICING.proMonthly[lang]}`, note: lang === "nl" ? "p/mnd" : "/mo" },
                { name: "Studio", nlName: "Studio", price: `€${TIER_PRICING.studioMonthly[lang]}`, note: lang === "nl" ? "p/mnd" : "/mo" },
              ].map((tier) => (
                <li key={tier.name} className="flex items-baseline gap-1.5">
                  <span className="font-data text-[0.95rem] text-brand-linen tabular-nums">
                    {tier.price}
                  </span>
                  <span className="text-[0.8rem] text-brand-linen/70">
                    {lang === "nl" ? tier.nlName : tier.name}
                  </span>
                  <span className="font-data text-[0.65rem] text-brand-muted tracking-[0.04em]">
                    {tier.note}
                  </span>
                </li>
              ))}
            </ul>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="flex flex-col sm:flex-row gap-3 max-w-[440px] mx-auto items-stretch sm:items-center justify-center">
              <Link
                href={START_HREF}
                onClick={() => trackCta("landing-close")}
                className="w-full sm:w-auto px-6 py-3 bg-brand-meadow text-on-accent text-sm font-semibold rounded-md hover:bg-brand-meadow-light active:scale-[0.98] transition-all cursor-pointer text-center"
              >
                {t("signup.submit")}
              </Link>
              <Link
                href="/signin"
                className="w-full sm:w-auto px-6 py-3 text-brand-linen text-sm font-medium rounded-md hover:text-brand-meadow transition-colors text-center"
              >
                {t("signup.signin")}
              </Link>
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="text-[0.75rem] text-brand-muted mt-4">
              {t("signup.privacy")}
            </p>
          </FadeIn>
          <FadeIn delay={0.22}>
            <Link
              href={lang === "nl" ? "/nl/pricing" : "/pricing"}
              className="inline-flex items-center gap-1.5 mt-4 text-[0.78rem] text-brand-sage hover:text-brand-meadow transition-colors"
            >
              {lang === "nl" ? "Wat zit er in elk plan?" : "See what's in each plan"}
              <svg viewBox="0 0 16 16" className="w-3 h-3 stroke-current fill-none" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </Link>
          </FadeIn>
        </div>
      </section>

      </div>
      </main>

      {/* ── Footer ── */}
      <SiteFooter locale={lang} />
    </div>
    </MotionConfig>
  );
}
